import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CATEGORIES, QUICK_REACTIONS, aboutEmoji, readRecent, rememberRecent, searchEmoji
} from '../lib/emoji';
import { TodoPlusIcon } from './todos/icons';

/**
 * THE EMOJI PICKER, as Slack, WhatsApp and Teams all draw one: a search box,
 * a row of category tabs, a grid, and a line at the foot naming whatever is
 * under the pointer.
 *
 * Gordon, 18 October: "add emojis to the chat in a similar fashion to slack,
 * whatsapp and teams". The same picker serves two purposes - the smiley
 * beside the paperclip puts an emoji in the box, and "add reaction" on a
 * message puts one under it - so it knows nothing about either. It is handed
 * an `anchor` to sit by and says what was picked.
 *
 * It closes on a pick, as Slack's does, unless Shift was held: Shift and
 * click keeps it open for "🎉🎉🎉". Escape closes it, and so does a click
 * anywhere else. The keyboard works the grid from the search box - arrows
 * move, Enter picks - so somebody who opened it with the keyboard never has
 * to reach for the mouse.
 *
 * ON A PHONE IT IS A SHEET from the foot of the screen, over the composer,
 * the width of the screen and never wider, with cells a thumb can hit. It
 * does not put the cursor in its search box there, because that would throw
 * the phone's keyboard up over the very grid somebody opened it to see.
 *
 * Drawn into <body> rather than where it is used: a message near the top of
 * a scrolled conversation would otherwise have its picker cut off by the
 * conversation's own edge.
 */

const narrow = () => {
  try { return window.matchMedia('(max-width: 900px)').matches; } catch { return false; }
};
const coarse = () => {
  try { return window.matchMedia('(pointer: coarse)').matches; } catch { return false; }
};

/** The smiley the composer and the message bar open the picker with. */
export const Smiley = ({ size = 18 }) => (
  <svg className="ch-smiley" viewBox="0 0 24 24" width={size} height={size} aria-hidden="true"
    focusable="false">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2" />
    <path d="M8.5 14.5a4.5 4.5 0 007 0" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" />
    <circle cx="9" cy="10" r="1.2" fill="currentColor" />
    <circle cx="15" cy="10" r="1.2" fill="currentColor" />
  </svg>
);

/** Columns in the grid as the browser laid it out, for ArrowUp and ArrowDown. */
const columnsOf = (grid) => {
  if (!grid) return 8;
  try {
    const cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
    return cols > 0 ? cols : 8;
  } catch {
    return 8;
  }
};

export function EmojiPicker({ anchor, mode = 'compose', label = 'Emoji', onPick, onClose }) {
  const [recent, setRecent] = useState(() => readRecent());
  // Recently used first when there are any, as everywhere else; otherwise
  // the faces, rather than an empty tab saying there is nothing yet.
  const [tab, setTab] = useState(() => (readRecent().length > 0 ? 'recent' : 'smileys'));
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [sheet] = useState(narrow);
  const [pos, setPos] = useState(null);
  const panel = useRef(null);
  const search = useRef(null);
  const grid = useRef(null);

  const searching = query.trim().length > 0;
  const shown = useMemo(() => {
    if (searching) return searchEmoji(query, 120);
    if (tab === 'recent') {
      return recent.map((c) => aboutEmoji(c) || { char: c, code: '', name: c });
    }
    return CATEGORIES.find((c) => c.id === tab)?.emoji || [];
  }, [searching, query, tab, recent]);

  useEffect(() => { setActive(0); }, [query, tab]);

  /*
   * Beside its anchor on a desktop: above it when there is room (the
   * composer is at the foot of the screen, so nearly always), below it
   * otherwise, and slid along so that no edge of it leaves the window.
   */
  useLayoutEffect(() => {
    if (sheet || !anchor) return undefined;
    const place = () => {
      // Across from the button, but clear of the whole composer it sits in,
      // so the picker never covers the words it is putting an emoji into.
      const clear = mode === 'compose' && anchor.closest ? (anchor.closest('form') || anchor) : anchor;
      const a = anchor.getBoundingClientRect();
      const c = clear.getBoundingClientRect();
      const r = { left: a.left, right: a.right, top: Math.min(a.top, c.top), bottom: Math.max(a.bottom, c.bottom) };
      const w = panel.current ? panel.current.offsetWidth : 340;
      const h = panel.current ? panel.current.offsetHeight : 380;
      const gap = 6;
      const want = mode === 'react' ? r.right - w : r.left;
      const left = Math.max(8, Math.min(want, window.innerWidth - w - 8));
      const above = r.top - gap - h >= 8 || r.top > window.innerHeight - r.bottom;
      setPos(above
        ? { left, bottom: window.innerHeight - r.top + gap }
        : { left, top: r.bottom + gap });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor, sheet, mode]);

  /*
   * The cursor in the search box, except on a touch screen (see above). Once
   * it has been placed: until then it is drawn invisibly to be measured, and
   * a box that cannot be seen cannot take the focus either.
   */
  const placed = sheet || pos !== null;
  const focused = useRef(false);
  useEffect(() => {
    if (!placed || focused.current) return;
    focused.current = true;
    if (!coarse() && search.current) search.current.focus({ preventScroll: true });
  }, [placed]);

  /*
   * A click anywhere else closes it - but not a click on whatever opened it,
   * which toggles it closed by itself and would otherwise open it again.
   */
  useEffect(() => {
    const away = (e) => {
      if (panel.current && panel.current.contains(e.target)) return;
      if (anchor && anchor.contains && anchor.contains(e.target)) return;
      onClose('away');
    };
    const esc = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onClose('escape');
    };
    document.addEventListener('mousedown', away, true);
    document.addEventListener('touchstart', away, true);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away, true);
      document.removeEventListener('touchstart', away, true);
      document.removeEventListener('keydown', esc);
    };
  }, [anchor, onClose]);

  // The highlighted cell kept in view as the arrows move it.
  useEffect(() => {
    const cell = grid.current?.querySelector('[data-active]');
    if (cell && cell.scrollIntoView) cell.scrollIntoView({ block: 'nearest' });
  }, [active, shown]);

  const choose = (e, keepOpen) => {
    if (!e) return;
    setRecent(rememberRecent(e.char));
    onPick(e.char, { keepOpen: Boolean(keepOpen) });
  };

  const keyDown = (e) => {
    const n = shown.length;
    if (e.key === 'Enter') {
      // Never the form's Enter: the picker sits over the composer.
      e.preventDefault();
      if (n > 0) choose(shown[Math.min(active, n - 1)], e.shiftKey);
      return;
    }
    if (!n) return;
    const inText = e.target === search.current && query.length > 0;
    const cols = columnsOf(grid.current);
    let next = null;
    if (e.key === 'ArrowDown') next = Math.min(n - 1, active + cols);
    else if (e.key === 'ArrowUp') next = Math.max(0, active - cols);
    else if (e.key === 'ArrowRight' && !inText) next = Math.min(n - 1, active + 1);
    else if (e.key === 'ArrowLeft' && !inText) next = Math.max(0, active - 1);
    if (next === null) return;
    e.preventDefault();
    setActive(next);
  };

  const current = shown[Math.min(active, shown.length - 1)] || null;
  const tabLabel = searching
    ? (shown.length ? 'Search results' : 'No emoji found')
    : CATEGORIES.find((c) => c.id === tab)?.label;

  const style = sheet ? undefined : (pos
    ? { left: pos.left, top: pos.top, bottom: pos.bottom }
    // Measured, invisibly, on the first frame before it is placed.
    : { left: 0, top: 0, visibility: 'hidden' });

  return createPortal(
    <>
      {sheet && <div className="ch-ep-scrim" aria-hidden="true" />}
      <div
        ref={panel}
        className="ch-ep"
        data-sheet={sheet || undefined}
        data-mode={mode}
        role="dialog"
        aria-label={label}
        style={style}
        onKeyDown={keyDown}
      >
        <input
          ref={search}
          className="ch-ep-search"
          type="search"
          placeholder="Search emoji"
          aria-label="Search emoji"
          autoComplete="off"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="ch-ep-tabs" role="tablist" aria-label="Emoji categories">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              className="ch-ep-tab"
              data-cat={c.id}
              aria-selected={!searching && tab === c.id}
              aria-label={c.label}
              title={c.label}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { setQuery(''); setTab(c.id); }}
            >
              {c.icon}
            </button>
          ))}
        </div>
        <p className="ch-ep-head">{tabLabel}</p>
        <div className="ch-ep-grid" ref={grid} aria-label={tabLabel}>
          {shown.map((e, i) => (
            <button
              key={`${e.char}-${i}`}
              type="button"
              className="ch-ep-cell"
              data-active={i === active || undefined}
              aria-label={e.name}
              title={e.code ? `:${e.code}:` : e.name}
              // Not taking the focus, so the cursor stays where it was -
              // in the search box, or in the message being written.
              onMouseDown={(ev) => ev.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={(ev) => choose(e, ev.shiftKey)}
            >
              {e.char}
            </button>
          ))}
          {!searching && tab === 'recent' && shown.length === 0 && (
            <p className="ch-ep-none">The ones you use will be kept here.</p>
          )}
          {searching && shown.length === 0 && (
            <p className="ch-ep-none">Nothing called that. Try another word.</p>
          )}
        </div>
        <div className="ch-ep-foot" aria-live="polite">
          {current ? (
            <>
              <span className="ch-ep-big" aria-hidden="true">{current.char}</span>
              <span className="ch-ep-name">
                {current.name}
                {current.code && <span className="ch-ep-code">:{current.code}:</span>}
              </span>
            </>
          ) : (
            <span className="ch-ep-name">Shift and click to pick several</span>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

/**
 * THE SIX QUICK REACTIONS - what the small smiley at the end of a message,
 * or a long press on a phone, brings up, as WhatsApp's does. "More" opens
 * the whole picker for the same message. "Make a to-do" (batch 4) sits under
 * them, the full width, when the page offers it; `onPick` null is a page
 * that offers only that.
 *
 * A SHEET ON A PHONE, A POPOVER ON A DESKTOP. Gordon, 21 October, asked for
 * the desktop to work "similar to the mobile view": so the same six, but
 * sitting by the smiley that opened them (`anchor`) rather than along the
 * foot of the screen - above it when there is room, below it otherwise, and
 * slid along so no edge leaves the window. The first emoji takes the focus
 * on a desktop, so somebody who opened it with the keyboard can pick with
 * the keyboard; Escape and a click elsewhere close it and hand the focus
 * back to the smiley. On a phone nothing takes the focus - the sheet is
 * tapped - and a scrim dims the conversation behind it.
 */
export function ReactionSheet({
  anchor = null, onPick, onMore, onTodo = null, onBranch = null, onEdit = null, onDelete = null, onClose
}) {
  const more = useRef(null);
  const panel = useRef(null);
  const first = useRef(null);
  const [sheet] = useState(() => narrow() || !anchor);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') { e.preventDefault(); onClose(); } };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  // By its smiley, kept on the screen. Measured once drawn, as the picker is.
  useLayoutEffect(() => {
    if (sheet || !anchor) return undefined;
    const place = () => {
      const a = anchor.getBoundingClientRect();
      const w = panel.current ? panel.current.offsetWidth : 300;
      const h = panel.current ? panel.current.offsetHeight : 90;
      const gap = 6;
      const left = Math.max(8, Math.min(a.left - 8, window.innerWidth - w - 8));
      const above = a.top - gap - h >= 8 || a.top > window.innerHeight - a.bottom;
      setPos(above
        ? { left, bottom: window.innerHeight - a.top + gap }
        : { left, top: a.bottom + gap });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [anchor, sheet]);

  // A click anywhere else closes a popover - not on the smiley that opened
  // it, which would only open it again. The phone's scrim does this job.
  useEffect(() => {
    if (sheet) return undefined;
    const away = (e) => {
      if (panel.current && panel.current.contains(e.target)) return;
      if (anchor && anchor.contains && anchor.contains(e.target)) return;
      onClose();
    };
    document.addEventListener('mousedown', away, true);
    return () => document.removeEventListener('mousedown', away, true);
  }, [anchor, sheet, onClose]);

  // The keyboard: the first emoji takes the focus once placed, and the
  // smiley gets it back when this goes.
  useEffect(() => {
    if (sheet || pos === null || coarse()) return undefined;
    const back = anchor;
    (first.current || more.current)?.focus({ preventScroll: true });
    return () => { if (back && document.contains(back)) back.focus({ preventScroll: true }); };
  }, [sheet, pos, anchor]);

  const style = sheet ? undefined : (pos
    ? { left: pos.left, top: pos.top, bottom: pos.bottom }
    : { left: 0, top: 0, visibility: 'hidden' });

  return createPortal(
    <>
      {sheet && <div className="ch-ep-scrim" aria-hidden="true" onClick={onClose} />}
      <div ref={panel} className="ch-react-sheet" data-pop={!sheet || undefined}
        role="dialog" aria-label="React to this message" style={style}>
        {onPick && QUICK_REACTIONS.map((emoji, i) => (
          <button key={emoji} ref={i === 0 ? first : undefined} type="button" className="ch-react-quick"
            aria-label={`React ${emoji}`} title={`React ${emoji}`} onClick={() => onPick(emoji)}>
            {emoji}
          </button>
        ))}
        {onPick && (
          <button ref={more} type="button" className="ch-react-quick ch-react-more"
            aria-label="More reactions" title="More reactions" onClick={() => onMore(more.current)}>
            <Smiley size={22} />
          </button>
        )}
        {/* Branch off, Edit and Delete (084): beside Make a to-do, so a
            phone's long press offers everything the desktop's icons do. */}
        {onBranch && (
          <button type="button" className="ch-sheet-todo ch-sheet-branch" onClick={onBranch}>
            <svg viewBox="0 0 16 16" width="18" height="18" aria-hidden="true" focusable="false">
              <path d="M6 3L2.5 6.5 6 10" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2.8 6.5H9a4 4 0 014 4V13" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
            </svg>
            <span>Branch off</span>
          </button>
        )}
        {onTodo && (
          <button type="button" className="ch-sheet-todo" onClick={onTodo}>
            <TodoPlusIcon size={18} />
            <span>Make a to-do</span>
          </button>
        )}
        {onEdit && (
          <button type="button" className="ch-sheet-todo ch-sheet-edit" onClick={onEdit}>
            <span aria-hidden="true" className="ch-sheet-glyph">✎</span>
            <span>Edit</span>
          </button>
        )}
        {onDelete && (
          <button type="button" className="ch-sheet-todo ch-sheet-delete" onClick={onDelete}>
            <span aria-hidden="true" className="ch-sheet-glyph">🗑</span>
            <span>Delete</span>
          </button>
        )}
      </div>
    </>,
    document.body
  );
}

export default EmojiPicker;
